import React, { useEffect, useState, useCallback } from "react";
import { CATEGORY_LABELS } from "@/lib/i18n";

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS);

/**
 * Experience edit / create modal.
 * - When `slug` is a non-empty string → edit mode (fetch by slug, PATCH on save).
 * - When `slug === ""` → create mode (empty form, POST on save). After a
 *   successful POST the modal transitions to edit mode using the returned
 *   slug so hero/gallery uploads become available.
 *
 * Form exposes explicit inputs for the full brief:
 *   - identity: title en/es, subtitle, country/city, duration
 *   - pricing: unit + inputs specific to the chosen unit + notes
 *   - narrative (bilingual)
 *   - highlights (bilingual editable lists)
 *   - categories (multi-select from the fixed catalog set)
 *   - hero + gallery uploads (edit mode only, requires a persisted slug)
 *   - a raw JSON textarea for the *rest* of the fields (perfect_for, partner,
 *     best_season, etc.) so admin can still edit anything without a mega form.
 */
export default function ExperienceEditModal({ slug, onClose, onSaved }) {
  // Local mirror of the "create" flag so we can flip to edit-mode after POST
  // without unmounting/remounting the modal.
  const [currentSlug, setCurrentSlug] = useState(slug);
  const isCreate = currentSlug === "";

  const [loading, setLoading] = useState(!isCreate);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);
  const [extraJson, setExtraJson] = useState("{}");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!currentSlug) {
      setDoc({
        slug: "",
        title: { en: "", es: "" },
        subtitle: { en: "", es: "" },
        country: "spain",
        city: "madrid",
        duration: "",
        narrative: { en: "", es: "" },
        highlights: { en: [], es: [] },
        categories: [],
        price_unit: "per_person",
        price: null,
        currency: "EUR",
        price_tiers: null,
        min_pax: null,
        max_pax: null,
        price_notes: { en: "", es: "" },
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/experiences/${currentSlug}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDoc(d);
      const simpleKeys = new Set([
        "id", "slug", "title", "subtitle", "country", "city", "duration",
        "narrative", "highlights", "categories",
        "price_unit", "price", "currency", "price_tiers", "min_pax", "max_pax", "price_notes",
        "hero_image", "gallery", "created_at", "visible", "status", "order",
      ]);
      const rest = {};
      Object.entries(d).forEach(([k, v]) => { if (!simpleKeys.has(k)) rest[k] = v; });
      setExtraJson(JSON.stringify(rest, null, 2));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [currentSlug]);
  useEffect(() => { load(); }, [load]);

  const patchField = (path, value) => {
    setDoc((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let ref = next;
      for (let i = 0; i < parts.length - 1; i += 1) {
        ref[parts[i]] = ref[parts[i]] ?? {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const toggleCategory = (cat) => {
    setDoc((prev) => {
      const next = structuredClone(prev);
      const cur = Array.isArray(next.categories) ? next.categories : [];
      next.categories = cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat];
      return next;
    });
  };

  const setHighlight = (lang, idx, value) => {
    setDoc((prev) => {
      const next = structuredClone(prev);
      next.highlights = next.highlights || { en: [], es: [] };
      next.highlights[lang] = [...(next.highlights[lang] || [])];
      next.highlights[lang][idx] = value;
      return next;
    });
  };
  const addHighlight = (lang) => {
    setDoc((prev) => {
      const next = structuredClone(prev);
      next.highlights = next.highlights || { en: [], es: [] };
      next.highlights[lang] = [...(next.highlights[lang] || []), ""];
      return next;
    });
  };
  const removeHighlight = (lang, idx) => {
    setDoc((prev) => {
      const next = structuredClone(prev);
      next.highlights = next.highlights || { en: [], es: [] };
      next.highlights[lang] = (next.highlights[lang] || []).filter((_, i) => i !== idx);
      return next;
    });
  };

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      let extras = {};
      try { extras = JSON.parse(extraJson || "{}"); } catch {
        throw new Error("Extra JSON is not valid JSON.");
      }
      const payload = { ...extras };
      [
        "title", "subtitle", "country", "city", "duration",
        "narrative", "highlights", "categories",
        "price_unit", "price", "currency", "price_tiers", "min_pax", "max_pax", "price_notes",
        "visible", "status",
      ].forEach((k) => {
        if (doc[k] !== undefined) payload[k] = doc[k];
      });
      // Strip empty highlights entries before submitting
      if (payload.highlights) {
        payload.highlights = {
          en: (payload.highlights.en || []).map((s) => (s || "").trim()).filter(Boolean),
          es: (payload.highlights.es || []).map((s) => (s || "").trim()).filter(Boolean),
        };
      }
      if (isCreate) {
        if (!doc.title?.en) throw new Error("Title (English) is required.");
        const r = await fetch(`${API}/api/admin/experiences`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error(`Create failed: HTTP ${r.status}`);
        const created = await r.json();
        // Transition to edit mode so image uploads are now possible.
        setCurrentSlug(created.slug);
        setDoc(created);
        onSaved && onSaved({ slug: created.slug, created: true, keepOpen: true });
      } else {
        const r = await fetch(`${API}/api/admin/experiences/${currentSlug}`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error(`Save failed: HTTP ${r.status}`);
        onSaved && onSaved({ slug: currentSlug });
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file, role) => {
    if (!file || !doc?.slug) return;
    const form = new FormData();
    form.append("file", file);
    const r = await fetch(
      `${API}/api/admin/experiences/${doc.slug}/images?role=${role}`,
      { method: "POST", credentials: "include", body: form },
    );
    if (!r.ok) {
      setError(`Upload failed: HTTP ${r.status}`);
      return;
    }
    await load();
  };
  const deleteImage = async (filename) => {
    if (!doc?.slug) return;
    if (!window.confirm(`Delete image ${filename}?`)) return;
    const r = await fetch(
      `${API}/api/admin/experiences/${doc.slug}/images/${filename}`,
      { method: "DELETE", credentials: "include" },
    );
    if (!r.ok) {
      setError(`Delete image failed: HTTP ${r.status}`);
      return;
    }
    await load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/70 flex items-start justify-center overflow-y-auto py-10" data-testid="admin-exp-modal">
      <div className="bg-ivory max-w-4xl w-full mx-4 shadow-xl">
        <div className="sticky top-0 bg-ivory border-b border-mist px-6 py-4 flex justify-between items-center z-10">
          <h3 className="font-display text-2xl text-charcoal" data-testid="admin-exp-modal-title">
            {isCreate ? "New experience" : `Edit — ${currentSlug}`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="eyebrow text-charcoal/60 hover:text-charcoal"
            data-testid="admin-exp-modal-close"
          >
            Close ×
          </button>
        </div>
        <div className="p-6 space-y-6">
          {loading && <p>Loading…</p>}
          {error && <div className="bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="admin-exp-modal-err">{error}</div>}
          {doc && !loading && (
            <>
              <FieldRow label="Title (EN) *">
                <input
                  type="text"
                  value={doc.title?.en || ""}
                  onChange={(e) => patchField("title.en", e.target.value)}
                  className="w-full px-3 py-2 border border-mist"
                  data-testid="admin-exp-title-en"
                />
              </FieldRow>
              <FieldRow label="Title (ES)">
                <input
                  type="text"
                  value={doc.title?.es || ""}
                  onChange={(e) => patchField("title.es", e.target.value)}
                  className="w-full px-3 py-2 border border-mist"
                  data-testid="admin-exp-title-es"
                />
              </FieldRow>
              <div className="grid grid-cols-2 gap-4">
                <FieldRow label="Country">
                  <select
                    value={doc.country || ""}
                    onChange={(e) => patchField("country", e.target.value)}
                    className="w-full px-3 py-2 border border-mist"
                    data-testid="admin-exp-country"
                  >
                    <option value="spain">Spain</option>
                    <option value="italy">Italy</option>
                    <option value="portugal">Portugal</option>
                  </select>
                </FieldRow>
                <FieldRow label="City slug">
                  <input
                    type="text"
                    value={doc.city || ""}
                    onChange={(e) => patchField("city", e.target.value)}
                    className="w-full px-3 py-2 border border-mist"
                    data-testid="admin-exp-city"
                  />
                </FieldRow>
              </div>
              <FieldRow label="Duration">
                <input
                  type="text"
                  value={doc.duration || ""}
                  onChange={(e) => patchField("duration", e.target.value)}
                  className="w-full px-3 py-2 border border-mist"
                  data-testid="admin-exp-duration"
                />
              </FieldRow>

              {/* Narrative */}
              <div className="border-t border-mist pt-4 mt-4">
                <h4 className="eyebrow text-brass mb-3">Narrative</h4>
                <FieldRow label="Narrative (EN)">
                  <textarea
                    value={doc.narrative?.en || ""}
                    onChange={(e) => patchField("narrative.en", e.target.value)}
                    className="w-full h-28 px-3 py-2 border border-mist text-sm"
                    data-testid="admin-exp-narrative-en"
                  />
                </FieldRow>
                <FieldRow label="Narrative (ES)">
                  <textarea
                    value={doc.narrative?.es || ""}
                    onChange={(e) => patchField("narrative.es", e.target.value)}
                    className="w-full h-28 px-3 py-2 border border-mist text-sm"
                    data-testid="admin-exp-narrative-es"
                  />
                </FieldRow>
              </div>

              {/* Highlights */}
              <div className="border-t border-mist pt-4 mt-4">
                <h4 className="eyebrow text-brass mb-3">Highlights</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <HighlightList
                    lang="en"
                    label="Highlights (EN)"
                    items={doc.highlights?.en || []}
                    onChange={setHighlight}
                    onAdd={addHighlight}
                    onRemove={removeHighlight}
                  />
                  <HighlightList
                    lang="es"
                    label="Highlights (ES)"
                    items={doc.highlights?.es || []}
                    onChange={setHighlight}
                    onAdd={addHighlight}
                    onRemove={removeHighlight}
                  />
                </div>
              </div>

              {/* Categories */}
              <div className="border-t border-mist pt-4 mt-4">
                <h4 className="eyebrow text-brass mb-3">Categories</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="admin-exp-categories">
                  {CATEGORY_KEYS.map((cat) => {
                    const on = (doc.categories || []).includes(cat);
                    return (
                      <label
                        key={cat}
                        className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm ${on ? "border-emerald bg-emerald/10 text-emerald" : "border-mist text-charcoal/70"}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleCategory(cat)}
                          data-testid={`admin-exp-category-${cat}`}
                        />
                        <span>{CATEGORY_LABELS[cat].en}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Pricing */}
              <div className="border-t border-mist pt-4 mt-4">
                <h4 className="eyebrow text-brass mb-3">Pricing</h4>
                <FieldRow label="Unit">
                  <select
                    value={doc.price_unit || "per_person"}
                    onChange={(e) => patchField("price_unit", e.target.value)}
                    className="w-full px-3 py-2 border border-mist"
                    data-testid="admin-exp-price-unit"
                  >
                    <option value="per_person">per_person</option>
                    <option value="per_group">per_group</option>
                    <option value="on_request">on_request</option>
                  </select>
                </FieldRow>
                {doc.price_unit === "per_person" && (
                  <FieldRow label="Price per person (€)">
                    <input
                      type="number"
                      step="0.01"
                      value={doc.price ?? ""}
                      onChange={(e) => patchField("price", e.target.value === "" ? null : Number(e.target.value))}
                      className="w-full px-3 py-2 border border-mist"
                      data-testid="admin-exp-price"
                    />
                  </FieldRow>
                )}
                {doc.price_unit === "per_group" && (
                  <FieldRow label="Price tiers (JSON: [{max_pax,price},...])">
                    <textarea
                      value={JSON.stringify(doc.price_tiers ?? [], null, 2)}
                      onChange={(e) => {
                        try {
                          patchField("price_tiers", JSON.parse(e.target.value));
                        } catch { /* keep typing */ }
                      }}
                      className="w-full h-32 font-mono text-xs px-3 py-2 border border-mist"
                      data-testid="admin-exp-price-tiers"
                    />
                  </FieldRow>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <FieldRow label="Min pax">
                    <input
                      type="number"
                      value={doc.min_pax ?? ""}
                      onChange={(e) => patchField("min_pax", e.target.value === "" ? null : Number(e.target.value))}
                      className="w-full px-3 py-2 border border-mist"
                    />
                  </FieldRow>
                  <FieldRow label="Max pax">
                    <input
                      type="number"
                      value={doc.max_pax ?? ""}
                      onChange={(e) => patchField("max_pax", e.target.value === "" ? null : Number(e.target.value))}
                      className="w-full px-3 py-2 border border-mist"
                    />
                  </FieldRow>
                </div>
                <FieldRow label="Price notes (EN)">
                  <input
                    type="text"
                    value={doc.price_notes?.en || ""}
                    onChange={(e) => patchField("price_notes.en", e.target.value)}
                    className="w-full px-3 py-2 border border-mist"
                  />
                </FieldRow>
                <FieldRow label="Price notes (ES)">
                  <input
                    type="text"
                    value={doc.price_notes?.es || ""}
                    onChange={(e) => patchField("price_notes.es", e.target.value)}
                    className="w-full px-3 py-2 border border-mist"
                  />
                </FieldRow>
              </div>

              {/* Images */}
              <div className="border-t border-mist pt-4 mt-4">
                <h4 className="eyebrow text-brass mb-3">Images</h4>
                {isCreate ? (
                  <p className="text-xs text-charcoal/60 italic-quote" data-testid="admin-exp-images-hint">
                    Save the experience first — image uploads unlock after the row exists.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-charcoal/70 mb-2">Hero</p>
                      {doc.hero_image ? (
                        <div className="relative">
                          <img src={doc.hero_image} alt="" className="w-full h-32 object-cover" />
                        </div>
                      ) : (<div className="w-full h-32 bg-mist" />)}
                      <label className="mt-2 block text-xs cursor-pointer text-brass hover:underline">
                        Upload new hero
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => uploadImage(e.target.files?.[0], "hero")}
                          data-testid="admin-exp-upload-hero"
                        />
                      </label>
                    </div>
                    <div>
                      <p className="text-sm text-charcoal/70 mb-2">Gallery ({(doc.gallery || []).length})</p>
                      <div className="grid grid-cols-3 gap-2">
                        {(doc.gallery || []).map((u) => {
                          const fname = u.split("/").pop();
                          return (
                            <div key={u} className="relative group">
                              <img src={u} alt="" className="w-full h-16 object-cover" />
                              <button
                                type="button"
                                onClick={() => deleteImage(fname)}
                                className="absolute inset-0 flex items-center justify-center bg-charcoal/70 text-ivory opacity-0 group-hover:opacity-100 transition text-xs"
                              >
                                Delete
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <label className="mt-2 block text-xs cursor-pointer text-brass hover:underline">
                        Add gallery image
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => uploadImage(e.target.files?.[0], "gallery")}
                          data-testid="admin-exp-upload-gallery"
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Other fields as raw JSON (edit mode) */}
              {!isCreate && (
                <div className="border-t border-mist pt-4 mt-4">
                  <h4 className="eyebrow text-brass mb-3">Other fields (raw JSON)</h4>
                  <p className="text-xs text-charcoal/60 mb-2">
                    partner, perfect_for, best_season, pull_quote, etc.
                  </p>
                  <textarea
                    value={extraJson}
                    onChange={(e) => setExtraJson(e.target.value)}
                    className="w-full h-48 font-mono text-xs px-3 py-2 border border-mist"
                    data-testid="admin-exp-extra-json"
                  />
                </div>
              )}
            </>
          )}
        </div>
        <div className="sticky bottom-0 bg-ivory border-t border-mist px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="eyebrow text-charcoal/70 hover:text-charcoal px-4 py-2"
            data-testid="admin-exp-cancel"
          >
            {isCreate ? "Cancel" : "Close"}
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="admin-exp-save"
            className="eyebrow bg-charcoal text-ivory px-6 py-2 hover:bg-charcoal/90 disabled:opacity-50"
          >
            {saving ? "Saving…" : (isCreate ? "Create" : "Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="eyebrow text-charcoal/70 text-xs block mb-1">{label}</span>
      {children}
    </label>
  );
}

function HighlightList({ lang, label, items, onChange, onAdd, onRemove }) {
  return (
    <div data-testid={`admin-exp-highlights-${lang}`}>
      <p className="eyebrow text-charcoal/70 text-xs mb-2">{label}</p>
      <ul className="space-y-2">
        {items.map((val, idx) => (
          <li key={`${lang}-${idx}`} className="flex gap-2">
            <input
              type="text"
              value={val}
              onChange={(e) => onChange(lang, idx, e.target.value)}
              className="flex-1 px-3 py-2 border border-mist text-sm"
              data-testid={`admin-exp-highlight-${lang}-${idx}`}
            />
            <button
              type="button"
              onClick={() => onRemove(lang, idx)}
              className="eyebrow text-red-700 text-xs px-2 hover:underline"
              data-testid={`admin-exp-highlight-remove-${lang}-${idx}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => onAdd(lang)}
        className="mt-3 eyebrow text-brass hover:underline text-xs"
        data-testid={`admin-exp-highlight-add-${lang}`}
      >
        + Add
      </button>
    </div>
  );
}
